import { c as _c } from "react/compiler-runtime";
import { FunnelStepId } from 'modules/funnels/funnel/types/funnel-step-interface';
import { useEffect, useRef, useState } from 'react';
import { useFetch } from 'shared/hooks/use-fetch';
import { RequestMethodsEnum } from 'shared/hooks/use-fetch/types';
import { FunnelId } from '../../types/funnel-interface';
import { useFunnelStepsList } from './use-funnel-steps-list';
interface BaseTempStateInterface {
  name: string;
}
export function useUpdateBaseConfigurationTab<R>({
  funnelStepId,
  funnelId,
  updateUrl
}: {
  funnelStepId: FunnelStepId;
  funnelId: FunnelId;
  updateUrl: string;
}) {
  const abortController = useRef<AbortController | null>(null);
  const {
    fetcher: updateBaseConfigurationFetcher
  } = useFetch<RequestMethodsEnum.put, R>({
    method: RequestMethodsEnum.put
  });
  const {
    mutateFunnelStepsList
  } = useFunnelStepsList(funnelId);
  const [isFetching, setIsFetching] = useState(false);
  const cancelRequest = () => {
    if (abortController.current) {
      abortController.current.abort();
    }
  };
  return {
    updateFetcher: async <T extends BaseTempStateInterface,>(updatedState: T, mutationAfterUpdate?: () => void | Promise<void>) => {
      try {
        setIsFetching(true);
        cancelRequest();
        abortController.current = new AbortController();
        await mutateFunnelStepsList(data => {
          if (data) {
            return data.map(dataFunnelStep => {
              if (dataFunnelStep.id === funnelStepId) {
                return {
                  ...dataFunnelStep,
                  name: updatedState.name
                };
              }
              return dataFunnelStep;
            });
          }
        }, false);
        const responseData = await updateBaseConfigurationFetcher(updateUrl, updatedState, {
          signal: abortController.current?.signal
        });
        if (responseData) {
          if (mutationAfterUpdate) mutationAfterUpdate();
          setIsFetching(false);
        }
      } catch (e) {
        setIsFetching(false);
        throw e;
      }
    },
    isFetching
  };
}
interface CommonFunnelStepConfigurationEditInterface {
  name: string;
  page?: {
    path: string;
  };
  webinar?: {
    duration: number;
  };
}
interface useBaseConfigurationTabFieldStateProps {
  savedState: CommonFunnelStepConfigurationEditInterface;
  currentState: CommonFunnelStepConfigurationEditInterface;
}
export const useBaseConfigurationTabFieldState = t0 => {
  const $ = _c(27);
  const {
    savedState,
    currentState
  } = t0;
  const [isNameChanged, setIsNameChanged] = useState(false);
  const [isPagePathChanged, setIsPagePathChanged] = useState(false);
  const [isWebinarDurationChanged, setIsWebinarDurationChanged] = useState(false);
  let t1;
  if ($[0] === Symbol.for("react.memo_cache_sentinel")) {
    t1 = {
      name: false,
      path: false,
      webinarDuration: false
    };
    $[0] = t1;
  } else {
    t1 = $[0];
  }
  const [successState, setSuccessState] = useState(t1);
  let t2;
  if ($[1] !== currentState.name || $[2] !== savedState.name) {
    t2 = () => {
      if (currentState.name !== savedState.name) {
        setIsNameChanged(true);
        setSuccessState(_temp);
      } else {
        setIsNameChanged(false);
      }
    };
    $[1] = currentState.name;
    $[2] = savedState.name;
    $[3] = t2;
  } else {
    t2 = $[3];
  }
  let t3;
  if ($[4] !== currentState || $[5] !== savedState) {
    t3 = [currentState, savedState];
    $[4] = currentState;
    $[5] = savedState;
    $[6] = t3;
  } else {
    t3 = $[6];
  }
  useEffect(t2, t3);
  let t4;
  if ($[7] !== currentState.page || $[8] !== savedState.page) {
    t4 = () => {
      if (currentState.page && savedState.page) {
        if (currentState.page.path !== savedState.page.path) {
          setIsPagePathChanged(true);
          setSuccessState(_temp2);
        } else {
          setIsPagePathChanged(false);
        }
      }
    };
    $[7] = currentState.page;
    $[8] = savedState.page;
    $[9] = t4;
  } else {
    t4 = $[9];
  }
  let t5;
  if ($[10] !== currentState || $[11] !== savedState) {
    t5 = [currentState, savedState];
    $[10] = currentState;
    $[11] = savedState;
    $[12] = t5;
  } else {
    t5 = $[12];
  }
  useEffect(t4, t5);
  let t6;
  if ($[13] !== currentState.webinar || $[14] !== savedState.webinar) {
    t6 = () => {
      if (currentState.webinar && savedState.webinar) {
        if (currentState.webinar.duration !== savedState.webinar.duration) {
          setIsWebinarDurationChanged(true);
          setSuccessState(_temp3);
        } else {
          setIsWebinarDurationChanged(false);
        }
      }
    };
    $[13] = currentState.webinar;
    $[14] = savedState.webinar;
    $[15] = t6;
  } else {
    t6 = $[15];
  }
  let t7;
  if ($[16] !== currentState || $[17] !== savedState) {
    t7 = [currentState, savedState];
    $[16] = currentState;
    $[17] = savedState;
    $[18] = t7;
  } else {
    t7 = $[18];
  }
  useEffect(t6, t7);
  let t8;
  let t9;
  if ($[19] !== successState) {
    t8 = () => {
      const cleanUpSuccessState = setTimeout(() => {
        if (successState.name || successState.path || successState.webinarDuration) {
          setSuccessState(_temp4);
        }
      }, 3000);
      return () => clearTimeout(cleanUpSuccessState);
    };
    t9 = [successState];
    $[19] = successState;
    $[20] = t8;
    $[21] = t9;
  } else {
    t8 = $[20];
    t9 = $[21];
  }
  useEffect(t8, t9);
  let t10;
  if ($[22] !== isNameChanged || $[23] !== isPagePathChanged || $[24] !== isWebinarDurationChanged || $[25] !== successState) {
    t10 = {
      isNameChanged,
      isPagePathChanged,
      isWebinarDurationChanged,
      successState,
      setSuccessState
    };
    $[22] = isNameChanged;
    $[23] = isPagePathChanged;
    $[24] = isWebinarDurationChanged;
    $[25] = successState;
    $[26] = t10;
  } else {
    t10 = $[26];
  }
  return t10;
};
function _temp(prev) {
  return {
    ...prev,
    name: false
  };
}
function _temp2(prev_0) {
  return {
    ...prev_0,
    path: false
  };
}
function _temp3(prev_1) {
  return {
    ...prev_1,
    webinarDuration: false
  };
}
function _temp4(prev_2) {
  return {
    ...prev_2,
    name: false,
    path: false,
    webinarDuration: false
  };
}